/**
 * The room's Progress: the project's agent runs, from its deployments.
 * A run going now leads; a queued run is one line; a scheduled one (a legacy
 * recurring deployment) says Scheduled. Every other run is a neutral row.
 *
 * The deployment row stays "queued" until the turn ends. The card follows the
 * latest AgentRun (polled) and, once that run is running, polls its steps.
 */
import { useEffect, useRef, useState } from "react";
import { Bot, Check, Plus, Square } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import ChatModelProviderMark from "@/app/routes/dashboard/components/ChatModelProviderMark";
import { getAiRequestErrorMessage } from "@/lib/api";
import { cn } from "@/utils/helpers";
import { queue } from "@/components/Toast";
import { useChatModels } from "@/hooks/useChatModels";
import { isDeploymentRunBusy, type WorkshopDeployment } from "@/types/workshop.type";
import { useStopWorkshopRun, useWorkshopRunSteps } from "../api/useWorkshopRunSteps";
import {
  useWorkshopDeploymentArtifacts,
  useWorkshopDeploymentRuns,
  useWorkshopDeployments,
} from "../api/useWorkshopDeployments";
import { formatMadeDate } from "../workshopOutputs";
import DeployAgentModal from "./DeployAgentModal";
import DeploymentDetailModal from "./DeploymentDetailModal";
import { WsInlineError, WsStateBlock } from "./WorkshopParts";

type RunState = "running" | "queued" | "scheduled" | "failed" | "done" | "never";

function runState(d: WorkshopDeployment): RunState {
  const status = d.lastRunStatus;
  if (status === "running") return "running";
  if (status === "queued") return "queued";
  if (status === "failed" || status === "error") return "failed";
  if (d.mode === "recurring" && d.enabled && d.nextRunAt) return "scheduled";
  if (!status) return "never";
  return "done";
}

const ORDER: Record<RunState, number> = {
  running: 0,
  queued: 1,
  scheduled: 2,
  failed: 3,
  done: 3,
  never: 4,
};

/** Tags colour by job: green only for a run going now, blue where it is scheduled. */
function RunTag({ state, children }: { state: RunState; children?: React.ReactNode }) {
  const label =
    state === "running"
      ? "Running"
      : state === "queued"
        ? "Queued"
        : state === "scheduled"
          ? "Scheduled"
          : state === "failed"
            ? "Failed"
            : state === "never"
              ? "Not run"
              : "Done";
  return (
    <span className="ws-tag ws-run-tag" data-state={state}>
      {state === "running" ? <span className="ws-run-live" aria-hidden="true" /> : null}
      {state === "done" || state === "queued" ? (
        <>
          {state === "queued" ? <span className="ws-run-glow" aria-hidden="true" /> : null}
          <Bot className="ws-run-bot" aria-hidden="true" />
          <span className="sr-only">{state === "queued" ? "Queued" : "Done"}</span>
        </>
      ) : (
        label
      )}
      {children}
    </span>
  );
}

function elapsed(fromIso: string, now: number): string {
  const seconds = Math.max(0, Math.floor((now - Date.parse(fromIso)) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * A run going now or waiting to (07 Workshop, row 43; Workshop / Run card).
 * Running: the tag with its clock, Stop run, the title, what it should find
 * out, the steps it has taken (the last one live), then the model and what it
 * has made. Queued: one line, its tag and its title.
 */
function LiveRun({
  projectId,
  deployment,
  state,
  unread,
  onOpen,
}: {
  projectId: string;
  deployment: WorkshopDeployment;
  state: "running" | "queued";
  unread: boolean;
  onOpen: () => void;
}) {
  const runs = useWorkshopDeploymentRuns(deployment.id, true);
  const listed = runs.data?.[0] ?? null;
  // Ignore a cached older run. The deployment names the turn that is live.
  const run = listed && listed.id === deployment.lastRunId ? listed : null;
  // Only a run that is actually running gets the progress card. A finished
  // run stays queued here until the deployment row catches up.
  const face: "running" | "queued" =
    run?.status === "running" || state === "running" ? "running" : "queued";
  const written = useWorkshopDeploymentArtifacts(deployment.id, true, face === "running");
  const { data: models = [] } = useChatModels();
  const steps = useWorkshopRunSteps(face === "running" ? deployment.id : null, run?.id ?? null);
  const stop = useStopWorkshopRun(projectId);
  const startedAt = face === "running" ? (run?.startedAt ?? null) : null;
  const [now, setNow] = useState(() => Date.now());
  const stepsRef = useRef<HTMLOListElement>(null);
  const lineCount = steps.data?.steps.length ?? 0;

  useEffect(() => {
    if (!startedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  useEffect(() => {
    const el = stepsRef.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: el.scrollHeight, behavior: reduce ? "auto" : "smooth" });
  }, [lineCount]);

  if (face === "queued") {
    return (
      <article className={cn("ws-run ws-run--queued", unread && "co-idea-row-glow")} data-state={face} data-deployment-id={deployment.id}>
        <button
          type="button"
          className="ws-run-hit"
          onClick={onOpen}
          aria-label={`${deployment.name}, queued. Open its run`}
        />
        <RunTag state={face} />
        <h3 className="ws-run-queued-title">{deployment.name}</h3>
      </article>
    );
  }

  const modelName = run?.model ? (models.find((m) => m.id === run.model)?.name ?? run.model) : null;
  const outputs = written.data?.length;
  const lines = steps.data?.steps ?? [];
  const foot = [
    lines.length > 0 ? `${lines.length} ${lines.length === 1 ? "step" : "steps"}` : null,
    outputs !== undefined
      ? outputs === 0
        ? "0 outputs yet"
        : `${outputs} ${outputs === 1 ? "output" : "outputs"}`
      : null,
  ].filter(Boolean);

  return (
    <article className={cn("ws-run", unread && "co-idea-row-glow")} data-state={face} data-deployment-id={deployment.id}>
      <button
        type="button"
        className="ws-run-hit"
        onClick={onOpen}
        aria-label={`${deployment.name}, running now. Open its run`}
      />
      <div className="ws-run-top">
        <RunTag state={face}>
          {startedAt ? <span className="ws-run-clock"> · {elapsed(startedAt, now)}</span> : null}
        </RunTag>
        <span className="ws-side-push" />
        {run && isDeploymentRunBusy(run.status) ? (
          <button
            type="button"
            className="ws-run-stop"
            disabled={stop.isPending}
            onClick={() => stop.mutate({ deploymentId: deployment.id, runId: run.id })}
          >
            <Square aria-hidden="true" />
            {stop.isPending ? "Stopping" : "Stop run"}
          </button>
        ) : null}
      </div>
      <h3 className="ws-run-title">{deployment.name}</h3>
      {deployment.objective ? <p className="ws-run-objective">{deployment.objective}</p> : null}
      {lines.length > 0 ? (
        <ol
          ref={stepsRef}
          className="ws-run-steps"
          aria-label="What it has done"
          onClick={onOpen}
        >
          {lines.map((step, index) => {
            const live = index === lines.length - 1 && !steps.data?.done;
            const origin = steps.data?.startedAt ?? lines[0]?.at ?? null;
            return (
              <li key={`${index}-${step.line}`} className="ws-run-step" data-live={live || undefined}>
                {live ? (
                  <span className="ws-run-step-live" aria-hidden="true">
                    <span />
                  </span>
                ) : (
                  <Check className="ws-run-step-check" aria-hidden="true" />
                )}
                <span className="ws-run-step-line">{step.line}</span>
                {origin ? (
                  <span className="ws-run-step-when">{elapsed(origin, Date.parse(step.at))}</span>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : null}
      <div className="ws-run-foot">
        {run?.model && modelName ? (
          <span className="ws-run-model">
            <ChatModelProviderMark modelId={run.model} size={12} />
            <span>{modelName}</span>
          </span>
        ) : null}
        {foot.length ? <span>{`${run?.model && modelName ? "· " : ""}${foot.join(" · ")}`}</span> : null}
      </div>
    </article>
  );
}

function RunRow({
  deployment,
  state,
  unread,
  onOpen,
}: {
  deployment: WorkshopDeployment;
  state: RunState;
  unread: boolean;
  onOpen: () => void;
}) {
  return (
    <li className="ws-run-row" data-deployment-id={deployment.id}>
      <button type="button" className={cn("ws-run-row-hit", unread && "co-idea-row-glow")} onClick={onOpen}>
        <RunTag state={state} />
        <span className="ws-run-row-name">{deployment.name}</span>
        {state === "scheduled" && deployment.nextRunAt ? (
          <span className="ws-meta">Next {formatMadeDate(deployment.nextRunAt)}</span>
        ) : null}
      </button>
    </li>
  );
}

type Props = {
  projectId: string;
  canWrite: boolean;
  onOpenArtifact?: (artifactId: string) => void;
  unopenedDeploymentIds?: string[];
  onOpenDeployment?: (deploymentId: string) => void;
};

export default function AgentsTab({
  projectId,
  canWrite,
  onOpenArtifact,
  unopenedDeploymentIds = [],
  onOpenDeployment,
}: Props) {
  const { deployments, isLoading, isError, refetch, createDeployment } =
    useWorkshopDeployments(projectId);
  const [deployOpen, setDeployOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // The open run stays fresh as the poll lands.
  const openDetail = detailId ? (deployments.find((d) => d.id === detailId) ?? null) : null;

  const open = (d: WorkshopDeployment) => {
    onOpenDeployment?.(d.id);
    setDetailId(d.id);
  };

  const sorted = [...deployments]
    .map((d) => ({ d, state: runState(d) }))
    .sort(
      (a, b) =>
        ORDER[a.state] - ORDER[b.state] ||
        Date.parse(b.d.lastRunAt ?? b.d.createdAt) - Date.parse(a.d.lastRunAt ?? a.d.createdAt),
    );
  const live = sorted.filter((x) => x.state === "running" || x.state === "queued");
  const rest = sorted.filter((x) => x.state !== "running" && x.state !== "queued");

  const deployButton = (primary: boolean) =>
    canWrite ? (
      <KitButton
        variant={primary ? "primary" : "secondary"}
        iconLeft={primary ? Plus : undefined}
        iconRight={primary ? undefined : Plus}
        className="ws-deploy-btn"
        onClick={() => setDeployOpen(true)}
      >
        Deploy an agent
      </KitButton>
    ) : null;

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="ws-run ws-run--skel" aria-busy="true" aria-label="Loading the runs">
        <span className="ws-skel ws-skel--tag" />
        <span className="ws-skel ws-skel--title" />
        <span className="ws-skel ws-skel--line" style={{ width: "80%" }} />
      </div>
    );
  } else if (isError) {
    body = (
      <WsInlineError
        className="ws-inline-error--compact"
        title="Couldn't load this project's runs"
        onRetry={() => void refetch()}
      />
    );
  } else if (deployments.length === 0) {
    body = (
      <WsStateBlock
        icon={Bot}
        className="ws-state--pane"
        title="No agents have run here yet"
        line="An agent runs once: it reads this project’s context and writes its outputs here, while you keep chatting."
        action={deployButton(true)}
      />
    );
  } else {
    body = (
      <>
        {live.map(({ d, state }) => (
          <div key={d.id}>
            <LiveRun
              projectId={projectId}
              deployment={d}
              state={state as "running" | "queued"}
              unread={unopenedDeploymentIds.includes(d.id)}
              onOpen={() => open(d)}
            />
          </div>
        ))}
        {rest.length > 0 ? (
          <ul className="ws-run-list" aria-label="Earlier runs">
            {rest.map(({ d, state }) => (
              <RunRow
                key={d.id}
                deployment={d}
                state={state}
                unread={unopenedDeploymentIds.includes(d.id)}
                onOpen={() => open(d)}
              />
            ))}
          </ul>
        ) : null}
        <div className="ws-run-deploy">
          <p>Agents run once, read this project's context and write outputs here.</p>
          {deployButton(false)}
        </div>
      </>
    );
  }

  return (
    <div className="ws-room-progress">
      {body}

      {deployOpen && canWrite ? (
        <DeployAgentModal
          onClose={() => setDeployOpen(false)}
          onSubmit={(vars, done) =>
            createDeployment.mutate(vars, {
              onSuccess: (result) => {
                setDeployOpen(false);
                done();
                queue.add({
                  title: "Agent deployed",
                  description: result.runId ? "Its run is queued." : "Deployment created.",
                  variant: "success",
                });
              },
              onError: (error) => done(getAiRequestErrorMessage(error)),
            })
          }
        />
      ) : null}

      <DeploymentDetailModal
        projectId={projectId}
        deployment={openDetail}
        canWrite={canWrite}
        onClose={() => setDetailId(null)}
        onOpenArtifact={onOpenArtifact}
      />
    </div>
  );
}
